import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
	Box,
	Button,
	Alert,
	Typography,
	TextField,
	FormControl,
	Select,
	MenuItem,
	InputLabel,
} from "@mui/material";
import * as hooks from "lib/hooks";
import * as result from "lib/result";
import { GetTextFieldStyle, GetSelectStyle } from "lib/styles/textField";
import { UseHasPermission } from "lib/hooks/useHasPermission";
import { useSelector } from "react-redux";
import { UserSlice } from "store";
import { BusinessObj } from "auth";
import {
	AccessibleSector,
	AccessiblePlant,
	CreatePlantUserRequest,
	SectorData,
} from "models/dto/user";
import { GMIDField } from "../Forms/GMIDField";
import { SectorTypePlantPermissionTable } from "../SectorTypePlantPermissionTable";
import { OBJECT_ID_MANAGE_USER } from "../../constants";
import type { SectorAdminRow, PlantUserRow } from "../../types";
import { UserManagementDialogShell } from "../UserManagementDialogShell";
import {
	BuildAlreadyHasAccessMessage,
	GetIncompleteSectorsWarning,
	GetIncompleteSectorsWarningMessage,
} from "../../utils/userAccessUtils";

interface formData {
	gmid: string;
	username: string;
	email: string;
	gmin: string;
	name: string;
	phone: string;
	phoneExt: string;
}

interface validationState {
	gmidExists: { valid: boolean; message: string };
}

interface addPlantUserDialogProps {
	open: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	/** Used to build a clearer "already has access" error (sector admin sectors for the user). */
	existingSectorAdmins?: SectorAdminRow[];
	/** Used to build a clearer "already has access" error (plant user sectors for the user). */
	existingPlantUsers?: PlantUserRow[];
}

export function AddPlantUserDialog({
	open,
	onClose,
	onSuccess,
	existingSectorAdmins = [],
	existingPlantUsers = [],
}: addPlantUserDialogProps) {
	const api = hooks.UseMccApi();
	const snack = hooks.UseSnackBar();
	const permissions = UseHasPermission();
	const businessObjList = useSelector(UserSlice.selectors.selectBusinessObjList) as BusinessObj[];

	const [formData, setFormData] = useState<formData>({
		gmid: "",
		username: "",
		email: "",
		gmin: "",
		name: "",
		phone: "",
		phoneExt: "",
	});

	const [validation, setValidation] = useState<validationState>({
		gmidExists: { valid: false, message: "" },
	});

	const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
	const [sectorData, setSectorData] = useState<Record<string, SectorData>>({});
	const [selectedSectorForAdd, setSelectedSectorForAdd] = useState<string>("");
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string>("");

	// Reset form when dialog opens/closes
	useEffect(() => {
		if (!open) {
			// Reset form when dialog closes
			setFormData({
				gmid: "",
				username: "",
				email: "",
				gmin: "",
				name: "",
				phone: "",
				phoneExt: "",
			});
			setValidation({
				gmidExists: { valid: false, message: "" },
			});
			setSelectedSectors([]);
			setSectorData({});
			setError("");
		}
	}, [open]);

	// Sectors where current user has Manage User (S1) — from permissions API (generic by objectId).
	const accessibleSectors: AccessibleSector[] = useMemo(
		() => permissions.getAccessibleSectorsForObject(OBJECT_ID_MANAGE_USER),
		[permissions.getAccessibleSectorsForObject]
	);

	// Plants from permissions API (S1 only), grouped by sector. No sector vs Plant Users Access Manager branching.
	const sectorTypePlants = useMemo(() => {
		const sectorNamesSet = new Set(
			accessibleSectors.map((s) => s.name)
		);
		const plantsBySector: Record<string, AccessiblePlant[]> = {};

		businessObjList.forEach((bo) => {
			if (bo.GetBusinessObjTypeString() !== OBJECT_ID_MANAGE_USER) return;

			const plantId = (bo as unknown as { readonly plantId: string }).plantId;
			const plantName = (bo as unknown as { readonly plantName: string }).plantName;
			const sectorName = bo.SectorName;

			if (!plantId || !plantName || !sectorName || !sectorNamesSet.has(sectorName))
				return;

			const sector = accessibleSectors.find((s) => s.name === sectorName);
			if (!sector) return;

			if (!plantsBySector[sector.id]) {
				plantsBySector[sector.id] = [];
			}
			if (!plantsBySector[sector.id].some((p) => p.id === plantId)) {
				plantsBySector[sector.id].push({ id: plantId, name: plantName });
			}
		});

		Object.keys(plantsBySector).forEach((sectorId) => {
			plantsBySector[sectorId].sort((a, b) => a.name.localeCompare(b.name));
		});

		return plantsBySector;
	}, [businessObjList, accessibleSectors]);

	const handleGMIDCheck = useCallback(async () => {
		if (!formData.gmid.trim()) {
			return { ok: false, msg: "Enter a GMID first" };
		}

		// Client-side format validation: 6 characters, alphanumeric only
		const trimmedGmid = formData.gmid.trim();
		if (trimmedGmid.length !== 6) {
			setValidation((prev) => ({
				...prev,
				gmidExists: {
					valid: false,
					message: "GMID must be exactly 6 characters",
				},
			}));
			return {
				ok: false,
				msg: "GMID must be exactly 6 characters",
			};
		}

		if (!/^[A-Za-z0-9]+$/.test(trimmedGmid)) {
			setValidation((prev) => ({
				...prev,
				gmidExists: {
					valid: false,
					message: "GMID must contain only letters and numbers",
				},
			}));
			return {
				ok: false,
				msg: "GMID must contain only letters and numbers",
			};
		}

		try {
			// Search for user in Azure directory first (to get user details)
			const users = await api.SearchGmid(trimmedGmid);
			if (!users.length) {
				setValidation((prev) => ({
					...prev,
					gmidExists: {
						valid: false,
						message: "No user found in directory",
					},
				}));
				return { ok: false, msg: "No user found in directory" };
			}

			const user = users[0];
			const gmin = user.employeeId || "";

			// Do not block on "user already exists" here: a user who is a Sector Admin in one
			// sector may be added as a Plant User in a different sector. If the backend
			// CreatePlantUser rejects (e.g. duplicate in same sector), we show a clear message on save.

			setValidation((prev) => ({
				...prev,
				gmidExists: { valid: true, message: "" },
			}));

			const newFormData = {
				...formData,
				username: user.onPremisesSamAccountName || formData.gmid.split("@")[0] || "",
				email: user.mail || "",
				gmin: gmin,
				name: user.displayName || "",
			};

			setFormData(newFormData);

			return { ok: true, msg: `Found ${user.displayName}` };
		} catch (err: any) {
			return {
				ok: false,
				msg: err?.response?.data?.message || err?.message || "Lookup failed",
			};
		}
	}, [formData, api]);

	const handleAddSector = useCallback(() => {
		if (!selectedSectorForAdd || selectedSectors.includes(selectedSectorForAdd)) {
			return;
		}
		setSelectedSectors((prev) => [...prev, selectedSectorForAdd]);
		setSectorData((prev) => ({
			...prev,
			[selectedSectorForAdd]: {
				sectorId: selectedSectorForAdd,
				plants: [],
			},
		}));
		setSelectedSectorForAdd("");
	}, [selectedSectorForAdd, selectedSectors]);

	const handleRemoveSector = useCallback((sectorId: string) => {
		setSelectedSectors((prev) => prev.filter((id) => id !== sectorId));
		setSectorData((prev) => {
			const newData = { ...prev };
			delete newData[sectorId];
			return newData;
		});
	}, []);

	const handleSectorDataChange = useCallback(
		(sectorId: string, data: SectorData) => {
			setSectorData((prev) => ({
				...prev,
				[sectorId]: data,
			}));
		},
		[]
	);

	// Base form valid: GMID lookup done and required fields present
	const isBaseFormValid = useMemo(() => {
		if (
			!formData.gmid ||
			!formData.username ||
			!formData.email ||
			!formData.gmin ||
			!formData.name
		) {
			return false;
		}
		return validation.gmidExists.valid;
	}, [formData, validation]);

	// Count of sectors that have at least one plant and every plant has at least one permission (same as Edit)
	const validSectorCount = useMemo(() => {
		return Object.values(sectorData).filter(
			(sector) =>
				sector.plants.length > 0 &&
				sector.plants.every((plant) => (plant.permissions?.length ?? 0) > 0)
		).length;
	}, [sectorData]);

	// Add enabled when base form valid and at least one valid sector (empty sectors are omitted on save)
	const isFormValid = isBaseFormValid && validSectorCount >= 1;

	const handleSave = useCallback(async () => {
		if (!isFormValid) return;

		try {
			setSaving(true);
			setError("");

			// Only send sectors where every plant has at least one permission (same as Edit)
			const validSectors = Object.values(sectorData).filter(
				(sector) =>
					sector.plants.length > 0 &&
					sector.plants.every((plant) => (plant.permissions?.length ?? 0) > 0)
			);

			// Block only when adding them to a sector they're already in (as Sector Admin or Plant User)
			const sectorsBeingAdded = validSectors.map((s) => s.sectorId);
			const saRow = existingSectorAdmins.find((r) => r.userId === formData.gmin);
			const puRows = existingPlantUsers.filter((r) => r.userId === formData.gmin);
			const sectorIdsAlreadyIn = new Set<string>([
				...(saRow?.sectorIds ?? []),
				...puRows.map((r) => r.sectorId),
			]);
			const overlap = sectorsBeingAdded.some((id) => sectorIdsAlreadyIn.has(id));
			if (overlap) {
				setError(
					BuildAlreadyHasAccessMessage(
						formData.gmin,
						existingSectorAdmins,
						existingPlantUsers
					)
				);
				setSaving(false);
				return;
			}

			// Add plant-user API: send only businessObjectId per permission (no permissionBits)
			const request: CreatePlantUserRequest = {
				username: formData.username,
				email: formData.email,
				gmin: formData.gmin,
				name: formData.name,
				phone: formData.phone || undefined,
				phoneExt: formData.phoneExt || undefined,
				sectors: validSectors.map((s) => ({
					sectorId: s.sectorId,
					plants: s.plants.map((p) => ({
						plantId: p.plantId,
						permissions: p.permissions.map((perm) => ({
							businessObjectId: perm.businessObjectId,
						})),
					})),
				})),
			};

			await api.CreatePlantUser(request);
			snack.open(result.Ok("Plant user created successfully"));
			onSuccess?.();
			onClose();
		} catch (err: any) {
			const rawMsg =
				err?.response?.data?.errorList?.[0]?.message ||
				err?.response?.data?.message ||
				err?.message ||
				"Failed to create plant user";
			const isDuplicateOrExists =
				/already exists|duplicate|user with this id/i.test(String(rawMsg));
			const sectorsWeTriedToAdd = Object.values(sectorData)
				.filter(
					(s) =>
						s.plants.length > 0 &&
						s.plants.every((p) => (p.permissions?.length ?? 0) > 0)
				)
				.map((s) => s.sectorId);
			const saRow = existingSectorAdmins.find((r) => r.userId === formData.gmin);
			const puRows = existingPlantUsers.filter((r) => r.userId === formData.gmin);
			const sectorIdsAlreadyIn = new Set<string>([
				...(saRow?.sectorIds ?? []),
				...puRows.map((r) => r.sectorId),
			]);
			const overlap = sectorsWeTriedToAdd.some((id) => sectorIdsAlreadyIn.has(id));
			const errorMsg =
				isDuplicateOrExists && overlap
					? BuildAlreadyHasAccessMessage(
							formData.gmin,
							existingSectorAdmins,
							existingPlantUsers
						)
					: rawMsg;
			setError(errorMsg);
		} finally {
			setSaving(false);
		}
	}, [
		isFormValid,
		formData,
		sectorData,
		api,
		snack,
		onClose,
		onSuccess,
		existingSectorAdmins,
		existingPlantUsers,
	]);

	return (
		<UserManagementDialogShell
			open={open}
			onClose={onClose}
			title="Add Plant User"
			maxWidth="md"
			error={error}
			contentSx={{ mt: 1 }}
			primaryButton={{
				label: "Add",
				onClick: handleSave,
				disabled: !isFormValid || saving,
				loading: saving,
			}}
		>
			{accessibleSectors.length === 0 ? (
				<Box display="flex" justifyContent="center" alignItems="center" p={3}>
					<Alert severity="info">No sectors available with manage user permissions.</Alert>
				</Box>
			) : (
				<>
					<GMIDField
							value={formData.gmid}
							setValue={(v) => {
							setFormData((prev) => ({ ...prev, gmid: v }));
							setValidation((prev) => ({
								...prev,
								gmidExists: { valid: false, message: "" },
							}));
							}}
							disabled={saving}
							editing={false}
							onClearError={() => setError("")}
							onCheckGMID={handleGMIDCheck}
						/>

						{/* GMIN Field - Read-only (populated from API) */}
						<TextField
							label="GMIN"
							value={formData.gmin}
							size="small"
							sx={GetTextFieldStyle()}
							InputProps={{
								sx: {
									height: 40,
									"& .MuiInputBase-input": { py: 1 },
									backgroundColor: "action.disabledBackground",
								},
								readOnly: true,
							}}
							InputLabelProps={{ shrink: true }}
							disabled
						/>

						{/* Email Field - Read-only (populated from API) */}
						<TextField
							label="Email"
							value={formData.email}
							size="small"
							sx={GetTextFieldStyle()}
							InputProps={{
								sx: {
									height: 40,
									"& .MuiInputBase-input": { py: 1 },
									backgroundColor: "action.disabledBackground",
								},
								readOnly: true,
							}}
							InputLabelProps={{ shrink: true }}
							disabled
						/>

						{/* Username Field - hidden but still validated */}
						<input type="hidden" value={formData.username} />

						{/* Sector Selection and Tables */}
						<Box>
							<Box
								display="flex"
								alignItems="center"
								gap={1}
								mb={2}
							>
								<Typography variant="body1" sx={{ minWidth: 80 }}>
									Sector:
								</Typography>
								<FormControl size="small" sx={{ minWidth: 120, ...GetSelectStyle() }}>
									<InputLabel>Sector</InputLabel>
									<Select
										value={selectedSectorForAdd}
										label="Sector"
										onChange={(e) => setSelectedSectorForAdd(e.target.value)}
										disabled={
											loading ||
											saving ||
											!accessibleSectors.some((s) => !selectedSectors.includes(s.id))
										}
									>
										{accessibleSectors
											.filter((s) => !selectedSectors.includes(s.id))
											.map((sector) => (
												<MenuItem key={sector.id} value={sector.id}>
													{sector.name}
												</MenuItem>
											))}
									</Select>
								</FormControl>
								<Button
									variant="contained"
									onClick={handleAddSector}
									disabled={
										!selectedSectorForAdd ||
										selectedSectors.includes(selectedSectorForAdd) ||
										loading ||
										saving
									}
									startIcon={<Typography sx={{ color: "white" }}>+</Typography>}
									sx={{ 
										color: "white",
										minWidth: 140,
									}}
								>
									Add Sector
								</Button>
							</Box>

							{selectedSectors.map((sectorId) => {
								const sector = accessibleSectors.find((s) => s.id === sectorId);
								if (!sector) return null;

								return (
								<SectorTypePlantPermissionTable
									key={sectorId}
									sector={sector}
									plants={sectorTypePlants[sectorId] || []}
										data={sectorData[sectorId] || { sectorId, plants: [] }}
										onDataChange={(data) =>
											handleSectorDataChange(sectorId, data)
										}
										onRemove={() => handleRemoveSector(sectorId)}
										isSectorAdmin={permissions.isSectorAdmin}
										loading={loading}
									/>
								);
							})}

							{selectedSectors.length === 0 && (
								<Alert severity="info">
									Click &quot;Add Sector&quot; to assign plants and permissions to this user.
								</Alert>
							)}

							{(() => {
							const incompleteWarning = GetIncompleteSectorsWarning(
								sectorData,
								accessibleSectors,
								sectorTypePlants
							);
								if (incompleteWarning.omittedSectorNames.length === 0) return null;
								return (
									<Alert severity="warning" sx={{ mt: 1 }}>
										{GetIncompleteSectorsWarningMessage(incompleteWarning)}
									</Alert>
								);
							})()}
						</Box>
				</>
			)}
		</UserManagementDialogShell>
	);
}


